import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { LineChart, Line, ResponsiveContainer } from 'recharts';

const cryptoData = [
  { 
    id: 1, rank: 1, name: 'Bitcoin', symbol: 'BTC', icon: 'fa-brands fa-bitcoin', iconColor: 'text-crypto-bitcoin bg-crypto-bitcoin/15',
    price: 67234.56, change24h: 2.34, change7d: 5.67, 
    marketCap: 1320000000000, volume: 28400000000,
    sparkline: Array.from({ length: 7 }, () => Math.random() * 100 + 65000),
    starred: true
  },
  { 
    id: 2, rank: 2, name: 'Ethereum', symbol: 'ETH', icon: 'fa-brands fa-ethereum', iconColor: 'text-crypto-ethereum bg-crypto-ethereum/15',
    price: 3245.78, change24h: -1.23, change7d: 3.45, 
    marketCap: 390000000000, volume: 15600000000,
    sparkline: Array.from({ length: 7 }, () => Math.random() * 100 + 3200),
    starred: true
  },
  { 
    id: 3, rank: 3, name: 'Solana', symbol: 'SOL', icon: 'fa-solid fa-sun', iconColor: 'text-purple-500 bg-purple-500/15',
    price: 142.56, change24h: 5.67, change7d: 12.34, 
    marketCap: 63000000000, volume: 4200000000,
    sparkline: Array.from({ length: 7 }, () => Math.random() * 10 + 135),
    starred: false
  },
  { 
    id: 4, rank: 4, name: 'BNB', symbol: 'BNB', icon: 'fa-solid fa-coins', iconColor: 'text-yellow-500 bg-yellow-500/15',
    price: 598.23, change24h: 0.89, change7d: 2.12, 
    marketCap: 89000000000, volume: 1800000000,
    sparkline: Array.from({ length: 7 }, () => Math.random() * 20 + 590),
    starred: false
  },
  { 
    id: 5, rank: 5, name: 'XRP', symbol: 'XRP', icon: 'fa-solid fa-circle-nodes', iconColor: 'text-gray-500 bg-gray-500/15',
    price: 0.5234, change24h: -2.45, change7d: -4.56, 
    marketCap: 28000000000, volume: 1200000000,
    sparkline: Array.from({ length: 7 }, () => Math.random() * 0.05 + 0.5),
    starred: false
  },
];

const MiniSparkline = ({ data, positive }) => {
  const chartData = data.map((value, index) => ({ value, index }));
  return (
    <div className="w-20 h-8">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData}>
          <Line
            type="monotone"
            dataKey="value"
            stroke={positive ? 'hsl(var(--success))' : 'hsl(var(--destructive))'}
            strokeWidth={1.5}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

const formatNumber = (num) => {
  if (num >= 1e12) return `$${(num / 1e12).toFixed(2)}T`;
  if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
  if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
  return `$${num.toLocaleString()}`;
};

export const MarketTable = ({ className, compact = false }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [starredItems, setStarredItems] = useState(
    cryptoData.filter(c => c.starred).map(c => c.id)
  );

  const toggleStar = (id, e) => {
    e.stopPropagation();
    setStarredItems(prev => 
      prev.includes(id) 
        ? prev.filter(item => item !== id)
        : [...prev, id]
    );
  };

  const filteredData = cryptoData.filter(coin =>
    coin.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    coin.symbol.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const displayData = compact ? filteredData.slice(0, 4) : filteredData;

  return (
    <Card className={cn("", className)}>
      <CardHeader className="pb-3 px-4 sm:px-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <CardTitle className="text-base">Market Overview</CardTitle>
          <div className="relative w-full sm:w-48">
            <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs"></i>
            <Input
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 h-8 text-sm"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-0 sm:px-2">
        <div className="overflow-x-auto scrollbar-thin">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-8 pl-4"></TableHead>
                <TableHead className="w-8">#</TableHead>
                <TableHead>Name</TableHead>
                <TableHead className="text-right">Price</TableHead>
                <TableHead className="text-right hidden sm:table-cell">24h</TableHead>
                <TableHead className="text-right hidden md:table-cell">7d</TableHead>
                <TableHead className="text-right hidden lg:table-cell">Market Cap</TableHead>
                <TableHead className="hidden xl:table-cell">Chart</TableHead>
                <TableHead className="w-20 pr-4"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayData.map((coin) => (
                <TableRow 
                  key={coin.id} 
                  className="group cursor-pointer"
                >
                  <TableCell className="pl-4">
                    <button
                      className="p-1 opacity-50 hover:opacity-100"
                      onClick={(e) => toggleStar(coin.id, e)}
                    >
                      <i className={cn(
                        "text-sm",
                        starredItems.includes(coin.id) 
                          ? "fa-solid fa-star text-crypto-bitcoin" 
                          : "fa-regular fa-star text-muted-foreground"
                      )}></i>
                    </button>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">{coin.rank}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className={cn("w-7 h-7 rounded-full flex items-center justify-center", coin.iconColor)}>
                        <i className={cn(coin.icon, "text-xs")}></i>
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{coin.name}</p>
                        <p className="text-xs text-muted-foreground font-mono">{coin.symbol}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    ${coin.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: coin.price < 1 ? 4 : 2 })}
                  </TableCell>
                  <TableCell className="text-right hidden sm:table-cell">
                    <Badge variant={coin.change24h >= 0 ? 'gain' : 'loss'} className="text-xs">
                      {coin.change24h >= 0 ? '+' : ''}{coin.change24h.toFixed(2)}%
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right hidden md:table-cell">
                    <span className={cn(
                      "font-mono text-sm",
                      coin.change7d >= 0 ? "text-success" : "text-destructive"
                    )}>
                      {coin.change7d >= 0 ? '+' : ''}{coin.change7d.toFixed(2)}%
                    </span>
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm text-muted-foreground hidden lg:table-cell">
                    {formatNumber(coin.marketCap)}
                  </TableCell>
                  <TableCell className="hidden xl:table-cell">
                    <MiniSparkline data={coin.sparkline} positive={coin.change7d >= 0} />
                  </TableCell>
                  <TableCell className="pr-4">
                    <Button variant="outline" size="sm" className="h-7 text-xs">
                      Trade
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default MarketTable;
